import { App } from 'vue'
import axios, { AxiosResponse, AxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios'
import VueAxios from 'vue-axios'
import store from '@/store'
import { Mutations } from '@/store/enums/StoreEnums'
import { i18n, DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/i18n/index'

/**
 * Points d'entrée où un 401 est une réponse métier, pas une session expirée.
 */
const AUTHENTICATION_ENDPOINT_PATHS = [
  '/authentication/v1.0/login',
  '/authentication/v1.0/login-dev',
  '/authentication/v1.0/magic-link',
  '/authentication/v1.0/first-connection',
  '/authentication/v1.0/reset-password',
  '/authentication/v1.0/forgot-password',
  '/authentication/v1.0/checkEmail',
  '/authentication/v1.0/oauth/token'
]

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  public static vueInstance: App;

  private static getCurrentLocale (): string {
    const i18nLocale = i18n.global.locale
    const localeValue = typeof i18nLocale === 'string' ? i18nLocale : i18nLocale.value
    const normalizedLocale = localeValue?.split('-')[0]
    const savedLocale = localStorage.getItem('locale')
    const candidateLocale = normalizedLocale || savedLocale || DEFAULT_LOCALE

    return SUPPORTED_LOCALES.includes(candidateLocale) ? candidateLocale : DEFAULT_LOCALE
  }

  /**
   * Vrai si l'URL appelée est un point d'entrée d'authentification, où un 401 est une
   * réponse métier — identifiants refusés, jeton de lien invalide — et non la marque
   * d'une session expirée.
   */
  private static isAuthenticationEndpoint (requestUrl: string | undefined): boolean {
    if (typeof requestUrl !== 'string' || requestUrl.trim().length === 0) {
      return false
    }

    return AUTHENTICATION_ENDPOINT_PATHS.some((endpointPath) => requestUrl.includes(endpointPath))
  }

  /**
   * @description Init. Service API
   */
  public static init (app: App<Element>) {
    ApiService.vueInstance = app
    ApiService.vueInstance.use(VueAxios, axios)
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_PUBLIC_API_URL
    // Si erreur 401 alors retirer le token et rediriger vers la page de login.
    // Les points d'entrée d'authentification sont exclus : un 401 y signifie
    // « identifiants refusés », et recharger la page empêcherait l'écran d'afficher
    // l'erreur.
    ApiService.vueInstance.axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError<unknown>) => {
        if (error.response?.status === 401 && !ApiService.isAuthenticationEndpoint(error.config?.url)) {
          store.commit(Mutations.LOGOUT_CURRENT_USER)
          // Déjà sur la page de connexion : ne pas boucler sur un rechargement.
          if (window.location.pathname !== '/') {
            window.location.assign('/')
          }
        }
        return Promise.reject(error)
      }
    )
    ApiService.vueInstance.axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError<any>) => {
        const formattedError = {
          status: error.response?.status,
          message:
            error.response?.data?.message ??
            error.response?.data?.data?.message ??
            'Une erreur est survenue.',
          errors: error.response?.data?.data?.errors ?? null
        }
        return Promise.reject(formattedError)
      }
    )
    ApiService.vueInstance.axios.interceptors.request.use(
      (config) => {
        const headers = config.headers as AxiosHeaders
        headers.set('Accept-Language', ApiService.getCurrentLocale())
        config.headers = headers
        return config
      }
    )
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader (): void {
    ApiService.vueInstance.axios.defaults.headers.common.Accept =
      'application/json'
    ApiService.vueInstance.axios.defaults.headers.common['Accept-Language'] =
      ApiService.getCurrentLocale()
  }

  /**
   * @description send the GET HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param params
   */
  public static get<T> (
    resource: string,
    params: AxiosRequestConfig | undefined = undefined
  ): Promise<AxiosResponse<T>> {
    return ApiService.vueInstance.axios.get(resource, params)
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param slug: string
   * @returns Promise<AxiosResponse>
   */
  // public static get<T> (
  //   resource: string,
  //   slug = ''
  // ): Promise<AxiosResponse<T>> {
  //   return ApiService.vueInstance.axios.get<T>(slug ? `${resource}/${slug}` : resource)
  // }

  /**
   * @description set the POST HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param payload
   * @param params
   */
  public static post<TPayload, TResponse> (
    resource: string,
    payload: TPayload,
    params: AxiosRequestConfig | undefined = undefined
  ): Promise<AxiosResponse<TResponse>> {
    return ApiService.vueInstance.axios.post(resource, payload, params)
  }

  // /**
  //  * @description send the UPDATE HTTP request
  //  * @param resource: string
  //  * @param slug: string
  //  * @param params: AxiosRequestConfig
  //  * @returns Promise<AxiosResponse>
  //  */
  // public static update(
  //     resource: string,
  //     slug: string,
  //     params: AxiosRequestConfig
  // ): Promise<AxiosResponse> {
  //     return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  // }

  // /**
  //  * @description Send the PUT HTTP request
  //  * @param resource: string
  //  * @param params: AxiosRequestConfig
  //  * @returns Promise<AxiosResponse>
  //  */
  // public static put(
  //     resource: string,
  //     params: AxiosRequestConfig
  // ): Promise<AxiosResponse> {
  //     return ApiService.vueInstance.axios.put(`${resource}`, params);
  // }

  // /**
  //  * @description Send the DELETE HTTP request
  //  * @param resource: string
  //  * @returns Promise<AxiosResponse>
  //  */
  // public static delete(resource: string): Promise<AxiosResponse> {
  //     return ApiService.vueInstance.axios.delete(resource);
  // }
}

export default ApiService
